import React, { useState, useEffect } from "react";

import { useTheme } from "@mui/material/styles";
import { Autocomplete, TextField } from "@mui/material";

import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up.svg";
import { ReactComponent as ChevronUpHover } from "assets/images/icons/icn_pagination_chevron_up_hover.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { ReactComponent as ChevronDownHover } from "assets/images/icons/icn_pagination_chevron_down_hover.svg";

const AutocompleteSelect = ({ width, placeholder = "", options, value, setValue }) => {
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const [sortedOptions, setSortedOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (options && options.length) {
      const sortedOptionsData = [...options].sort((a, b) =>
        a.label.localeCompare(b.label, "en", { sensitivity: "base" })
      );
      setSortedOptions(sortedOptionsData);
    }
  }, [options]);

  return (
    <Autocomplete
      sx={{
        width,
        '& .MuiOutlinedInput-root': {
          height: 29,
          borderRadius: theme.shape.customBorderRadius[1],
          '& fieldset': {
            borderColor: theme.palette.primary.greyFifth,
            borderWidth: 1,
          },
          '&:hover fieldset': {
            borderColor: theme.palette.primary.mainHover,
            borderWidth: 1,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.mainHover,
            borderWidth: 1,
          },
        },
        '& .MuiAutocomplete-popupIndicator': {
          display: 'none',
        },
        '& input::placeholder': {
          color: theme.palette.primary.greyThird,
          opacity: 1,
        },
      }}
      componentsProps={{
        popper: {
          sx: {
            '& .MuiAutocomplete-noOptions': {
              paddingLeft: "10px",
              margin: "5px 5px 0 0",
              fontSize: theme.typography.customFontSize[2],
              color: theme.palette.primary.main,
            },
            '& .MuiAutocomplete-listbox': {
              paddingTop: "5px",
              paddingLeft: "10px",
              paddingRight: "10px",
              boxSizing: "content-box",
              marginRight: "10px",
              maxHeight: "180px",
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.primary.main,
                borderRadius: theme.shape.customBorderRadius[0],
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: theme.palette.primary.mainHover,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.primary.greyThread,
                borderRadius: theme.shape.customBorderRadius[0],
                margin: "10px 0",
              },
              "&::-webkit-scrollbar-button": {
                display: "none",
              },
              '& .MuiAutocomplete-option[aria-selected="true"]': {
                backgroundColor: theme.palette.primary.lightBlue,
              },
              '& li': {
                color: theme.palette.primary.main,
                fontSize: theme.typography.customFontSize[2],
                paddingLeft: "10px",
                margin: "5px 5px 0 0",
                borderRadius: theme.shape.customBorderRadius[0],
                height: "40px",
                '&:hover': {
                  backgroundColor: theme.palette.primary.lightBlue,
                },
              },
              '& li.Mui-focused': {
                backgroundColor: theme.palette.primary.lightBlue,
              },
              '& li.Mui-selected': {
                backgroundColor: theme.palette.primary.lightBlue,
                '&.Mui-focused': {
                  backgroundColor: theme.palette.primary.lightBlue,
                },
              },
            },
          },
        },
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      options={sortedOptions}
      value={value}
      inputValue={inputValue}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        if (option && typeof option === "object" && option.label) {
          return option.label;
        }
        return "";
      }}
      // 嚴謹搜尋 一個字一個字搜尋 必須要一模一樣 才會搜尋的到
      filterOptions={(options, { inputValue }) => {
        if (!inputValue) return options;

        const keyword = inputValue.toLowerCase();

        return options.filter(option => {
          const label = option.label.toLowerCase();
          if (keyword.length > label.length) return false;
          for (let i = 0; i < keyword.length; i++) {
            if (label[i] !== keyword[i]) {
              return false;
            }
          }

          return true;
        });
      }}
      onChange={(_event, newValue) => setValue(newValue)}
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === "input") {
          setOpen(true);
        }

        if (reason === "clear") {
          setOpen(false);
        }
      }}
      autoHighlight
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            style: {
              fontSize: theme.typography.customFontSize[2],
              color: theme.palette.primary.greyPrimary,
              fontWeight: theme.typography.customFontWeight[3],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: "Open Sans",
              lineHeight: 1.5,
              paddingLeft: 4,
            },
            endAdornment: (
              <span
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                  display: "flex",
                  cursor: "pointer",
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
                onClick={() => setOpen((prev) => !prev)}
              >
                {open ? (hover ? <ChevronUpHover /> : <ChevronUp />)
                  : (hover ? <ChevronDownHover /> : <ChevronDown />)}
              </span>
            ),
          }}
        />
      )}
    />
  );
}

export default AutocompleteSelect
